(() => {
  if (window.NavelisFacets) return;
  window.NavelisFacets = true;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const desktopMedia = window.matchMedia('(min-width: 990px)');
  let activeDrawer = null;
  let drawerReturnFocus = null;

  // Each discovery section owns its request and bounded progressive page window.
  const states = new WeakMap();
  const maxLoadedPages = 5;
  const stateFor = (section) => {
    if (!states.has(section)) states.set(section, {
      sequence: 0, controller: null, loading: false, pages: 1, animation: null,
      automatic: section.dataset.paginationMode === 'infinite', observer: null,
      loadedUrls: new Set([window.location.href]), currentUrl: window.location.href, snapshots: new Map(),
    });
    return states.get(section);
  };
  const animateResults = (section, entering) => {
    const state = stateFor(section);
    state.animation?.cancel();
    state.animation = null;
    const grid = section.querySelector('[data-discovery-grid]');
    if (!grid?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('motion--none')) return;
    // Fade the old grid while the request runs; reveal the new one without delaying it.
    const animation = grid.animate(entering
      ? [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }]
      : [{ opacity: getComputedStyle(grid).opacity }, { opacity: 0.35 }], {
      duration: entering ? 240 : 140, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: entering ? 'none' : 'forwards',
    });
    state.animation = animation;
    animation.finished.then(() => {
      if (entering && state.animation === animation) state.animation = null;
    }, () => {});
  };
  const canonicalUrl = (url) => {
    const result = new URL(url, window.location.origin);
    result.searchParams.delete('section_id');
    result.hash = '';
    return result.href;
  };
  const rememberResults = (section) => {
    const state = stateFor(section);
    const results = section.querySelector('[data-discovery-results]');
    const url = canonicalUrl(state.currentUrl);
    // A serialized copy cannot inherit the original cart request's completion.
    // Re-fetch this window instead of restoring a permanently busy quick-add.
    if (results?.querySelector('[data-quick-add-button][data-loading="true"], [data-quick-add-button][aria-busy="true"]')) {
      state.snapshots.delete(url);
      return;
    }
    const html = results?.outerHTML;
    // Keep at most four recent windows, with a strict per-window memory ceiling.
    if (!html || html.length > 750000) return;
    state.snapshots.delete(url);
    state.snapshots.set(url, { html, pages: state.pages, loadedUrls: [...state.loadedUrls], scrollY: window.scrollY });
    while (state.snapshots.size > 4) state.snapshots.delete(state.snapshots.keys().next().value);
  };
  const restoreResults = (section, snapshot) => {
    const fragment = new DOMParser().parseFromString(snapshot.html, 'text/html');
    const results = fragment.querySelector('#DiscoveryResults-' + CSS.escape(section.dataset.sectionId));
    if (!results) return null;
    results.querySelectorAll('[data-bound]').forEach((element) => delete element.dataset.bound);
    results.querySelectorAll('[data-facets]').forEach((facets) => {
      const desktopSlot = facets.querySelector('[data-facets-desktop-slot]');
      const form = facets.querySelector('[data-facet-form]');
      if (form && desktopSlot) desktopSlot.append(form);
      const drawer = facets.querySelector('[data-facets-drawer]');
      if (drawer) drawer.hidden = true;
    });
    return results;
  };
  const setLoading = (section, loading) => {
    if (loading) section.setAttribute('aria-busy', 'true');
    else section.removeAttribute('aria-busy');
    section.querySelector('[data-discovery-loading]')?.toggleAttribute('hidden', !loading);
    const link = section.querySelector('[data-load-more]');
    if (loading) link?.setAttribute('aria-disabled', 'true');
    else link?.removeAttribute('aria-disabled');
  };
  const showError = (section, moveFocus = true) => {
    const error = section.querySelector('[data-discovery-error]');
    if (!error) return;
    error.hidden = false;
    if (moveFocus) error.focus();
  };
  const beginRequest = (section) => {
    const state = stateFor(section);
    state.animation?.cancel();
    state.animation = null;
    state.controller?.abort();
    state.observer?.disconnect();
    state.controller = new AbortController();
    state.sequence += 1;
    state.loading = true;
    setLoading(section, true);
    section.querySelector('[data-discovery-error]')?.setAttribute('hidden', '');
    return { state, sequence: state.sequence, signal: state.controller.signal };
  };
  const readResults = async (section, url, request) => {
    const requestUrl = new URL(url, window.location.origin);
    if (requestUrl.origin !== window.location.origin) throw new Error('Unexpected discovery origin');
    requestUrl.searchParams.set('section_id', section.dataset.sectionId);
    const response = await fetch(requestUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: request.signal,
    });
    if (!response.ok) throw new Error('Discovery request failed');
    const html = await response.text();
    if (request.sequence !== request.state.sequence || request.signal.aborted) return null;
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const results = fragment.querySelector('#DiscoveryResults-' + CSS.escape(section.dataset.sectionId));
    if (!results) throw new Error('Discovery response was incomplete');
    return results;
  };
  const finishRequest = (section, request) => {
    if (request.sequence !== request.state.sequence) return;
    request.state.loading = false;
    setLoading(section, false);
    initializeProgressive(section);
  };
  const initializeProgressive = (section) => {
    const state = stateFor(section);
    state.observer?.disconnect();
    state.observer = null;
    const mode = section.dataset.paginationMode;
    const controls = section.querySelector('[data-progressive-controls]');
    const next = section.querySelector('[data-load-more]');
    const pagination = section.querySelector('[data-discovery-pagination]');
    const enabled = mode === 'load_more' || mode === 'infinite';
    const canLoad = enabled && !!next && state.pages < maxLoadedPages;
    if (controls) controls.hidden = !canLoad;
    if (pagination) pagination.hidden = canLoad;
    const limit = section.querySelector('[data-progressive-limit]');
    if (limit) limit.hidden = !(enabled && next && state.pages >= maxLoadedPages);
    const toggle = section.querySelector('[data-auto-toggle]');
    if (toggle) {
      toggle.hidden = !canLoad || mode !== 'infinite' || typeof IntersectionObserver === 'undefined';
      toggle.setAttribute('aria-pressed', String(!state.automatic));
      toggle.textContent = state.automatic ? toggle.dataset.pauseLabel : toggle.dataset.resumeLabel;
    }
    const sentinel = section.querySelector('[data-progressive-sentinel]');
    if (canLoad && mode === 'infinite' && state.automatic && !state.loading && sentinel && typeof IntersectionObserver !== 'undefined') {
      state.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting) && state.automatic && !state.loading) loadNextPage(section, true);
      }, { rootMargin: '200px' });
      state.observer.observe(sentinel);
    }
  };
  const updateSection = async (section, url, pushState = true) => {
    if (activeDrawer?.closest('[data-discovery-section]') === section) closeDrawer(false);
    rememberResults(section);
    const request = beginRequest(section);
    animateResults(section, false);
    try {
      const snapshot = !pushState && request.state.snapshots.get(canonicalUrl(url));
      const nextResults = snapshot ? restoreResults(section, snapshot) : await readResults(section, url, request);
      if (!nextResults) return;
      const currentResults = section.querySelector('[data-discovery-results]');
      if (!currentResults) throw new Error('Discovery results missing');
      currentResults.replaceWith(nextResults);
      animateResults(section, true);
      request.state.pages = snapshot ? snapshot.pages : 1;
      request.state.loadedUrls = new Set(snapshot ? snapshot.loadedUrls : [canonicalUrl(url)]);
      request.state.currentUrl = canonicalUrl(url);
      if (!pushState) request.state.automatic = false;
      if (pushState) window.history.pushState({}, '', url);
      initializeDrawers(section);
      if (snapshot) window.requestAnimationFrame(() => window.scrollTo({ top: snapshot.scrollY, behavior: 'instant' }));
      else section.querySelector('[data-discovery-status]')?.focus({ preventScroll: true });
    } catch (error) {
      if (request.sequence === request.state.sequence && !request.signal.aborted) {
        request.state.animation?.cancel();
        request.state.animation = null;
        showError(section);
      }
    } finally {
      finishRequest(section, request);
    }
  };
  const loadNextPage = async (section, automatic = false) => {
    const state = stateFor(section);
    const next = section.querySelector('[data-load-more]');
    if (!next || state.loading || state.pages >= maxLoadedPages) return;
    const url = canonicalUrl(next.href);
    if (state.loadedUrls.has(url)) {
      state.automatic = false;
      initializeProgressive(section);
      showError(section, !automatic);
      return;
    }
    rememberResults(section);
    const request = beginRequest(section);
    try {
      const nextResults = await readResults(section, url, request);
      if (!nextResults) return;
      const currentGrid = section.querySelector('[data-discovery-grid]');
      const nextGrid = nextResults.querySelector('[data-discovery-grid]');
      const currentControls = section.querySelector('[data-discovery-page-controls]');
      const nextControls = nextResults.querySelector('[data-discovery-page-controls]');
      if (!currentGrid || !nextGrid?.children.length || !currentControls || !nextControls) throw new Error('Incomplete progressive page');
      const existingIds = new Set([...currentGrid.querySelectorAll('[id]')].map((element) => element.id));
      for (const element of nextGrid.querySelectorAll('[id]')) {
        if (existingIds.has(element.id)) throw new Error('Duplicate product control IDs');
        existingIds.add(element.id);
      }
      const added = [...nextGrid.children];
      const focusedControl = currentControls.contains(document.activeElement)
        ? (document.activeElement.matches('[data-auto-toggle]') ? '[data-auto-toggle]' : '[data-load-more]') : null;
      currentGrid.append(...added);
      currentControls.replaceWith(nextControls);
      state.pages += 1;
      state.loadedUrls.add(url);
      state.currentUrl = url;
      window.history.pushState({}, '', url);
      const status = section.querySelector('[data-progressive-status]');
      if (status) status.textContent = status.dataset.loadedMessage.replace('[count]', String(currentGrid.querySelectorAll('.product-card').length));
      initializeProgressive(section);
      if (!automatic) added[0].querySelector('a[href]')?.focus({ preventScroll: true });
      else if (focusedControl) {
        const control = section.querySelector(focusedControl);
        if (control && !control.closest('[hidden]')) control.focus({ preventScroll: true });
        else status?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (request.sequence === state.sequence && !request.signal.aborted) {
        state.automatic = false;
        showError(section, !automatic);
      }
    } finally {
      finishRequest(section, request);
    }
  };

  const closeDrawer = (restoreFocus = true) => {
    if (!activeDrawer) return;
    const mobileSlot = activeDrawer.querySelector('[data-facets-mobile-slot]');
    const desktopSlot = activeDrawer.closest('[data-facets]')?.querySelector('[data-facets-desktop-slot]');
    const form = mobileSlot?.querySelector('[data-facet-form]');
    if (form && desktopSlot) desktopSlot.append(form);
    activeDrawer.hidden = true;
    document.documentElement.classList.remove('facets-open');
    drawerReturnFocus?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) drawerReturnFocus?.focus();
    activeDrawer = null;
    drawerReturnFocus = null;
  };

  const openDrawer = (facets, trigger) => {
    const drawer = facets.querySelector('[data-facets-drawer]');
    const form = facets.querySelector('[data-facet-form]');
    const mobileSlot = drawer?.querySelector('[data-facets-mobile-slot]');
    if (!drawer || !form || !mobileSlot) return;
    mobileSlot.append(form);
    drawer.hidden = false;
    document.documentElement.classList.add('facets-open');
    activeDrawer = drawer;
    drawerReturnFocus = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    drawer.querySelector(focusableSelector)?.focus();
  };

  const initializeDrawers = (scope = document) => {
    scope.querySelectorAll('[data-facets]').forEach((facets) => {
      const trigger = facets.querySelector('[data-facets-open]');
      if (trigger && !trigger.dataset.bound) {
        trigger.dataset.bound = 'true';
        trigger.addEventListener('click', () => openDrawer(facets, trigger));
      }
      facets.querySelectorAll('[data-facets-close]').forEach((button) => {
        if (button.dataset.bound) return;
        button.dataset.bound = 'true';
        button.addEventListener('click', closeDrawer);
      });
    });
  };

  const restoreDesktopForms = () => {
    if (!desktopMedia.matches) return;
    if (activeDrawer) closeDrawer(false);

    document.querySelectorAll('[data-facets]').forEach((facets) => {
      const mobileSlot = facets.querySelector('[data-facets-mobile-slot]');
      const desktopSlot = facets.querySelector('[data-facets-desktop-slot]');
      const drawer = facets.querySelector('[data-facets-drawer]');
      const form = mobileSlot?.querySelector('[data-facet-form]');
      if (form && desktopSlot) desktopSlot.append(form);
      if (drawer) drawer.hidden = true;
    });
    document.documentElement.classList.remove('facets-open');
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facet-form], [data-sort-form]');
    if (!form) return;
    const section = form.closest('[data-discovery-section]');
    if (!section) return;
    event.preventDefault();
    closeDrawer();
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const url = new URL(form.action, window.location.origin);
    url.search = params.toString();
    url.hash = '';
    updateSection(section, url.href);
  });

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (sort) { sort.form?.requestSubmit(); return; }
    // Desktop facets apply as they change; the mobile drawer keeps its Apply step.
    const control = event.target.closest('[data-facet-form] input, [data-facet-form] select');
    if (control && desktopMedia.matches && !activeDrawer) control.form?.requestSubmit();
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const toggle = event.target.closest('[data-auto-toggle]');
    if (toggle) {
      const section = toggle.closest('[data-discovery-section]');
      if (!section || section.dataset.paginationMode !== 'infinite') return;
      event.preventDefault();
      const state = stateFor(section);
      state.automatic = !state.automatic;
      initializeProgressive(section);
      return;
    }
    const more = event.target.closest('[data-load-more]');
    if (more) {
      const section = more.closest('[data-discovery-section]');
      if (!section || !['load_more', 'infinite'].includes(section.dataset.paginationMode)) return;
      event.preventDefault();
      loadNextPage(section);
      return;
    }
    const link = event.target.closest('[data-discovery-results] .facets__chip, [data-discovery-results] .facets__clear, [data-discovery-pagination] a');
    if (!link) return;
    const section = link.closest('[data-discovery-section]');
    if (!section) return;
    event.preventDefault();
    updateSection(section, link.href);
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...activeDrawer.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-discovery-section]').forEach((section) => updateSection(section, window.location.href, false));
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeDrawers(event.target);
    event.target.querySelectorAll('[data-discovery-section]').forEach(initializeProgressive);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-discovery-section]').forEach((section) => {
      const state = states.get(section);
      if (state) {
        state.animation?.cancel();
        state.sequence += 1;
        state.controller?.abort();
        state.observer?.disconnect();
      }
      if (activeDrawer?.closest('[data-discovery-section]') === section) closeDrawer(false);
    });
  });
  desktopMedia.addEventListener('change', restoreDesktopForms);

  initializeDrawers();
  restoreDesktopForms();
  document.querySelectorAll('[data-discovery-section]').forEach(initializeProgressive);
})();
